import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FiChevronDown, FiX } from 'react-icons/fi';
import useNavigationRail from 'modules/Navigation/_utils/useNavigationRail';
import { groupsPropType, badgesPropType } from './propTypes';
import useNavItemAccess from './useNavItemAccess';
import * as Styled from './SubNavMobile.styles';

const findItem = (groups, key) => {
  for (let i = 0; i < groups.length; i += 1) {
    const found = groups[i].items.find((it) => it.key === key);
    if (found) return found;
  }
  return null;
};

/**
 * Full-screen sheet selector used as the mobile fallback for SubNav.
 */
const SubNavMobile = ({ groups, activeKey, activeKeys, badges, onSelect, title }) => {
  const [open, setOpen] = useState(false);
  // The desktop rail stays on screen from 900px; between that and 1024px this sheet sits beside it.
  const { width: railWidth } = useNavigationRail();
  const activeItem = findItem(groups, activeKey);
  const ActiveIcon = activeItem?.icon;
  // Items to highlight in the sheet. Defaults to the trigger's `activeKey`, but a combined sheet
  // (e.g. Views + Status) can pass several so each independent axis shows its own selection.
  const highlightedKeys = activeKeys.length ? activeKeys : [activeKey];

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Role permission AND account module — see useNavItemAccess.
  const hasPermission = useNavItemAccess();

  const handleSelect = (key) => {
    setOpen(false);
    onSelect(key);
  };

  return (
    <>
      <Styled.Trigger type="button" open={open} onClick={() => setOpen((v) => !v)}>
        {ActiveIcon && <ActiveIcon className="lead" />}
        <span className="label">{activeItem?.label || 'Select section'}</span>
        <FiChevronDown className="chevron" />
      </Styled.Trigger>

      {open && (
        <Styled.Sheet role="dialog" aria-modal="true" aria-label={title} $railWidth={railWidth}>
          <Styled.SheetHeader>
            <Styled.SheetTitle>{title}</Styled.SheetTitle>
            <Styled.SheetClose type="button" aria-label="Close" onClick={() => setOpen(false)}>
              <FiX />
            </Styled.SheetClose>
          </Styled.SheetHeader>
          {groups.map((group) => {
            const visibleItems = group.items.filter(hasPermission);
            if (!visibleItems.length) return null;
            return (
              <Styled.SheetGroup key={group.key}>
                <Styled.SheetGroupLabel>{group.label}</Styled.SheetGroupLabel>
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const badge = badges[item.key];
                  return (
                    <Styled.SheetItem
                      key={item.key}
                      type="button"
                      active={highlightedKeys.includes(item.key)}
                      sub={item.sub}
                      onClick={() => handleSelect(item.key)}
                    >
                      {Icon && <Icon />}
                      <span>{item.label}</span>
                      {badge != null && <Styled.SheetItemBadge tone={badge.tone}>{badge.value}</Styled.SheetItemBadge>}
                    </Styled.SheetItem>
                  );
                })}
              </Styled.SheetGroup>
            );
          })}
        </Styled.Sheet>
      )}
    </>
  );
};

SubNavMobile.propTypes = {
  groups: groupsPropType.isRequired,
  activeKey: PropTypes.string.isRequired,
  activeKeys: PropTypes.arrayOf(PropTypes.string),
  badges: badgesPropType,
  onSelect: PropTypes.func.isRequired,
  title: PropTypes.string,
};

SubNavMobile.defaultProps = {
  activeKeys: [],
  badges: {},
  title: 'Sections',
};

export default SubNavMobile;
